import Link from 'next/link'

interface RezaroLogoProps {
  variant?: 'light' | 'dark'
  size?: 'sm' | 'md' | 'lg'
  showWordmark?: boolean
  showTagline?: boolean
  /** Home link target — locale-aware (e.g. /en, /tr) when set. */
  href?: string
}

/**
 * Official brand lockup (July 2026 rebrand): the skewed-polygon "r" mark
 * plus "ezaro" as ONE wordmark image, extracted from the brand PDF.
 *  - variant 'light' → green wordmark for light surfaces (/logo-wordmark.png)
 *  - variant 'dark'  → white wordmark for dark surfaces (/logo-wordmark-white.png)
 *  - showWordmark=false → mark only (/logo-mark.png), e.g. tight mobile headers
 * PNGs are 1200px wide (~4.43:1), rendered by height so retina stays crisp.
 */
export function RezaroLogo({
  variant = 'light',
  size = 'md',
  showWordmark = true,
  showTagline = false,
  href = '/',
}: RezaroLogoProps) {
  const heights = { sm: 20, md: 26, lg: 38 }
  const h = heights[size]
  const taglineColor = variant === 'dark' ? 'rgba(255,255,255,0.55)' : 'var(--color-pub-muted)'

  const src = showWordmark
    ? (variant === 'dark' ? '/logo-wordmark-white.png' : '/logo-wordmark.png')
    : '/logo-mark.png'
  // Wordmark aspect ≈ 4.43:1; the mark alone is roughly square.
  const w = showWordmark ? Math.round(h * 4.43) : h

  return (
    <Link href={href} className="inline-flex items-center transition-opacity hover:opacity-85">
      <span className="flex flex-col leading-none">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt="Rezaro" width={w} height={h}
          style={{ height: h, width: 'auto', display: 'block' }} />
        {showTagline && (
          <span className="mt-1.5 font-medium tracking-wide uppercase"
            style={{ fontSize: size === 'lg' ? 12 : 10, color: taglineColor, letterSpacing: '0.12em' }}>
            Reservierungssystem
          </span>
        )}
      </span>
    </Link>
  )
}

/** Mark-only variant of the brand logo (green polygon "r"). */
export function LogoMark({ size = 32 }: { size?: number }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src="/logo-mark.png" alt="Rezaro" width={size} height={size}
    style={{ height: size, width: 'auto', flexShrink: 0 }} />
}
